import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "../../custom/sub-header/sub-header";
import BirthplaceField from "../../custom/birthplace-field/birthplace-field";
import UserService from "../../../service/user-service";
import AuthService from "../../../service/auth-service";
import useAuth from "../../../hooks/useAuth";
import {
  TIME_UNKNOWN,
  formatBirthDate,
  formatBirthTime,
  isBirthDateComplete,
  isBirthDateValid,
  isBirthTimeValid,
  missingChartFields,
  normalizeBirthPlace,
} from "../../../utils/birth-input";
import "./personal-editor-page-style.scss";

//個人資料編輯
//
//  這一頁在 my-page 早就有兩個入口指著（含那句「尚未填寫生日與性別，前往補齊」），
//  但 /personal-editor 這條 route 一直不存在 —— 點下去是白頁。2026-10-28 補上。
//
//  —— 為什麼生辰要收在帳號上 ——
//
//  原本生辰只活在「買報告」那條動線裡（order.intake），一張訂單一份。
//  但提問（白夜）是「就著你的命盤問」，它沒有訂單可以依附；
//  鏡月與緋紅之後也要同一份資料。收在帳號上，全站才共用同一張盤。
//
//  四項缺一不可：日、時、地、性別。
//  出生地是為了真太陽時（同一個時鐘時間，台北和喀什的太陽位置差快兩小時），
//  性別決定大運順逆 —— 少了任何一項，排出來的盤就是另一個人的。
//
//  —— 允許存一半 ——
//  只想改暱稱的人不該被逼著把生辰填完。所以「存檔」只擋**格式錯的**欄位，
//  不擋「還沒填」；命理服務需要哪些，用下面那條提示講清楚，由使用者自己決定。
//  ⚠️ 只給男／女，沒有「其他」。
//
//  不是疏漏：八字的大運順行或逆行是由「陽年男、陰年女順行」這條規則決定的，
//  命理上只有兩種取法，沒有第三種算法可以對應。五支產品的問診也都只給這兩個
//  （見各 product page 的 gender 那一幕）。
//
//  models/user 的 enum 裡還留著 "2"，但這裡不給 —— 選得到卻排不出盤的話，
//  使用者會以為自己填完了，然後拿到一張用預設值算出來的別人的命盤。
//  要支援的話得先決定「其他」在排盤時取哪一種，那是產品決定不是介面問題。
const GENDERS = [
  { value: "0", label: "女" },
  { value: "1", label: "男" },
];

const PersonalEditorPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [birthTime, setBirthTime] = useState("");
  const [place, setPlace] = useState(null);
  //出生地打了字卻還沒從清單裡選中。擋存檔用 —— 見下方 handleSave 的說明
  const [placePending, setPlacePending] = useState(false);
  const [gender, setGender] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  //把帳號上現有的值灌進表單。
  //  依賴 user 而不是只跑一次：useAuth 的值是從 localStorage 讀的，
  //  這一頁可能在它同步好之前就掛上了（見 hooks/useAuth 的說明）。
  useEffect(() => {
    if (!user) return;
    setName(user.userName || "");
    setBirthDate(user.userBirthDate || "");
    setBirthTime(user.userBirthTime || "");
    setGender(user.userGender || "");
    //座標缺一就當作沒填 —— 只有地名的話排不出真太陽時
    setPlace(normalizeBirthPlace(user.userBirthPlace));
  }, [user]);

  const unknownTime = birthTime === TIME_UNKNOWN;

  //—— 存檔擋的是「填錯」，不是「沒填」——
  //  空的日期與時間照樣存得起來（代表還沒填）；
  //  但 2026/02/30 或 25:99 這種填了卻不成立的，存進去只會在排盤時才爆。
  const dateBroken = Boolean(birthDate) && !isBirthDateValid(birthDate);
  const timeBroken = Boolean(birthTime) && !isBirthTimeValid(birthTime);
  //  出生地打了字卻沒選中（搜不到、或搜到了但還沒點）也算「填錯」。
  //  不擋的話會存進一個空的出生地，而畫面上明明有字 ——
  //  使用者會以為存好了，下次打開才發現不見（Kevin 2026-10-30 回報）。
  const placeBroken = placePending;

  //命理服務還缺什麼。四項缺一張盤就排不出來。
  //  判斷走共用的 missingChartFields —— 提問那邊的攔截吃的是同一支，
  //  各寫一份就會出現「這裡說填好了，提問還是擋你」。
  const missing = useMemo(
    () =>
      missingChartFields({
        birthDate,
        birthTime,
        birthPlace: place,
        gender,
      }),
    [birthDate, birthTime, place, gender],
  );

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving || dateBroken || timeBroken || placeBroken) return;

    setSaving(true);
    setError("");
    try {
      const res = await UserService.updateProfile({
        userName: name.trim(),
        userBirthDate: birthDate,
        userBirthTime: birthTime,
        //清掉的話送整包空值，後端才知道是「清掉」而不是「沒有要改」
        userBirthPlace: place
          ? { label: place.label, lat: place.lat, lng: place.lng }
          : { label: "", lat: null, lng: null },
        userGender: gender,
      });

      //  後端回的是整份 user，直接拿它覆寫本地那一份 ——
      //  自己拼一份的話，哪天後端多存了一個欄位，前端這份就會少一塊。
      const saved = res.data?.data?.user;
      if (saved) AuthService.updateUserInfo(saved);

      //  回上一頁而不是固定回 my-page：從提問被帶過來的人要回得去提問
      //  （見 baiye-chat 的資料完整性攔截）。沒有上一頁才退回 my-page。
      if (window.history.state?.idx > 0) navigate(-1);
      else navigate("/my-page", { replace: true });
    } catch (err) {
      //  留在原地並說明原因 —— 他剛打完一整份資料，不能讓它消失
      setError(err?.response?.data?.message || "儲存失敗，請稍後再試一次。");
      setSaving(false);
    }
  };

  return (
    <>
      <SubHeader title="個人資料" fallback="/my-page" />

      <main className="pe">
        {/* noValidate：瀏覽器的原生驗證要關掉。
            下面兩個輸入框掛著 pattern="[0-9]*"，那是**給 iOS 判斷要不要
            出數字鍵台**用的，不是真的格式規則 —— 而日期與時間的值帶著
            斜線與冒號，對不上那個 pattern，送出時會被原生驗證擋下來並
            跳出「請符合要求的格式」。
            白夜問診頁同樣寫法卻沒事，因為那裡的輸入框不在 form 裡。
            格式我們自己驗（isBirthDateValid / isBirthTimeValid），
            所以這裡請瀏覽器讓開。 */}
        <form className="pe__form container" onSubmit={handleSave} noValidate>
          <label className="pe__field">
            <span className="pe__label">暱稱</span>
            <input
              className="pe__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="你希望他怎麼稱呼你"
              autoComplete="nickname"
            />
          </label>

          {/* 日期。type 是 text 而不是 date —— 原生日期選擇器在各家瀏覽器
              長得完全不一樣，而且要選到 1993 年得往回捲三十幾次。
              直接打數字最快，斜線由 formatBirthDate 自動補。 */}
          <label className="pe__field">
            <span className="pe__label">出生年月日</span>
            <div className="pe__row">
              <input
                className="pe__input"
                type="text"
                //手機要跳純數字鍵盤，這兩個缺一不可：
                //  inputMode 決定 Android 給哪種鍵盤，
                //  pattern 則是 iOS 判斷「要不要給無字母鍵台」的依據。
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="bday"
                placeholder="ex: 1993/01/28"
                value={birthDate}
                onChange={(e) => setBirthDate(formatBirthDate(e.target.value))}
              />
              {/* 標示這裡收的是國曆，不是可以切換的開關 —— 所以用 span 不用 button */}
              <span className="pe__tag">國曆</span>
            </div>

            {/* 只在「八碼填滿但日子不存在」時出現。
                填到一半就開始糾正很煩人，而存檔鈕按不下去卻不說原因更煩。 */}
            {isBirthDateComplete(birthDate) && !isBirthDateValid(birthDate) && (
              <p className="pe__hint pe__hint--bad">這一天不存在，再確認一下</p>
            )}
          </label>

          <label className="pe__field">
            <span className="pe__label">出生時間</span>
            <div className="pe__row">
              <input
                className="pe__input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="ex: 14:30"
                //選了「不知道」就把欄位空出來並鎖住 ——
                //  讓一個不會被採用的時間留在畫面上，只會讓人以為它還算數。
                value={unknownTime ? "" : birthTime}
                disabled={unknownTime}
                onChange={(e) => setBirthTime(formatBirthTime(e.target.value))}
              />

              {/* 這顆是真的開關（和上面的「國曆」不同），所以用 button。
                  再按一次可以取消 —— 誤觸之後要回得來。 */}
              <button
                type="button"
                className={`pe__toggle${unknownTime ? " is-active" : ""}`}
                aria-pressed={unknownTime}
                onClick={() => setBirthTime(unknownTime ? "" : TIME_UNKNOWN)}
              >
                不知道時間
              </button>
            </div>

            <p className="pe__hint">
              不知道也沒關係，少的是時柱，年月日三柱照樣批得出來。
            </p>
          </label>

          <div className="pe__field">
            <span className="pe__label">出生地</span>
            <BirthplaceField
              value={place}
              onChange={setPlace}
              onPendingChange={setPlacePending}
              placeholder="ex: 台北市"
            />
            <p className="pe__hint">出生地決定真太陽時，會影響時柱的判定。</p>
          </div>

          <div className="pe__field">
            <span className="pe__label">性別</span>
            <div className="pe__genders" role="radiogroup" aria-label="性別">
              {GENDERS.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  role="radio"
                  aria-checked={gender === g.value}
                  className={`pe__gender${gender === g.value ? " is-active" : ""}`}
                  onClick={() => setGender(g.value)}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <p className="pe__hint">性別決定大運的順行或逆行。</p>
          </div>

          {/* 還缺什麼。講清楚但不擋存檔 —— 只想改暱稱的人不該被逼著填完生辰 */}
          {missing.length > 0 && (
            <p className="pe__missing">命理服務還需要：{missing.join("、")}</p>
          )}

          {error && <p className="pe__error">{error}</p>}

          <button
            type="submit"
            className="pe__save"
            //只擋填錯的，不擋沒填
            disabled={saving || dateBroken || timeBroken || placeBroken}
          >
            {saving ? "儲存中…" : "儲存"}
          </button>
        </form>
      </main>
    </>
  );
};

export default PersonalEditorPage;
